import { WaterfallStructure } from 'further-types/lp/carry-settings';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { waterfallStructureLabels } from 'further-ui/labels';
import { numberToCurrencyString } from 'further-ui/utils';

const returnOfCapitalTooltip =
  "This step is the minimum of each investor's (i) paid-in capital or (ii) gross proceeds to date";
const preferredReturnDistributableTooltip =
  "This step is the minimum of each investor's (i) accrued preferred return or (ii) gross proceeds to date";

type Props = Array<{
  commitmentId: string;
  waterfall: {
    preferredReturnAccrued: {
      accruedPreferredReturn: number;
    };
    catchUpAccrued: number;
    waterfallElements: Array<{
      element: WaterfallStructure;
      value: number;
    }>;
  };
}>;

export default function useWaterfallElements(
  fundsToDistribute: Props,
  baseCurrency: CurrencyCode,
) {
  const waterfallSteps = (
    fundsToDistribute[0]?.waterfall.waterfallElements.map(
      (waterfallElement, stepIndex) => {
        if (waterfallElement.element === WaterfallStructure.PreferredReturn) {
          return [
            {
              key: 'preferredReturnAccrued',
              label: 'Pref. return (accrued)',
              width: 200,
              tooltip:
                "This step is the total preferred return that has accrued to date within the fund based on each investor's paid in capital and preferred return rate.",
            },
            {
              key: waterfallElement.element,
              label: `Pref. return (distributable)`,
              width: 200,
              tooltip:
                preferredReturnDistributableTooltip +
                (stepIndex > 0
                  ? ' (after deducting amounts allocated in previous steps)'
                  : '') +
                '.',
            },
          ];
        }
        if (
          waterfallElement.element ===
          WaterfallStructure.CarriedInterestPartnerCatchup
        ) {
          return [
            {
              key: 'catchUpAccrued',
              label: 'Catch-up (accrued)',
              width: 200,
              tooltip:
                "This step is the total carried interest partner catch-up that has accrued to date within the fund based on each investor's accrued preferred return using the formula: $accruedpreferredreturn * ($carryrate%/1-$carryrate%)",
            },
            {
              key: waterfallElement.element,
              label: `Catch-up (distributable)`,
              width: 200,
              tooltip:
                'This step is the minimum of (i) the accrued carried interest partner catch-up or (ii) the gross proceeds to date (after deducting amounts allocated in previous steps).',
            },
          ];
        }

        if (waterfallElement.element === WaterfallStructure.ExcessProceeds) {
          return [
            {
              key: waterfallElement.element,
              label: waterfallStructureLabels[waterfallElement.element],
              width: 200,
              tooltip:
                'This step is the remaining gross proceeds after deducting all amounts allocated for distribution in the previous steps.',
            },
          ];
        }

        if (waterfallElement.element === WaterfallStructure.ReturnOfCapital) {
          return [
            {
              key: waterfallElement.element,
              label: waterfallStructureLabels[waterfallElement.element],
              width: 200,
              tooltip: `${returnOfCapitalTooltip}${
                stepIndex > 0
                  ? ' (after deducting amounts allocated in previous steps)'
                  : ''
              }.`,
            },
          ];
        }

        return [
          {
            key: waterfallElement.element,
            label: waterfallStructureLabels[waterfallElement.element],
            width: 200,
          },
        ];
      },
    ) ?? []
  ).flat();

  const getColumns = () => waterfallSteps;

  const getValues = (commitmenId: string) => {
    const waterfallElements =
      fundsToDistribute.find((fund) => fund.commitmentId === commitmenId)
        ?.waterfall.waterfallElements ?? [];

    return Object.fromEntries(
      waterfallElements.map((waterfallElement) => {
        return [
          waterfallElement.element,
          numberToCurrencyString(waterfallElement.value, {
            currency: baseCurrency,
          }),
        ];
      }),
    );
  };

  return {
    getColumns,
    getValues,
  };
}
